'use client';

import React, { useEffect, useState } from 'react';
import { Body, Heading, Modal, ProfileModalImage, Tag } from '@stereoagency/ui';
import useTranslation from 'next-translate/useTranslation';
import { defaultLanguage } from '@stereoagency/constants';
import { FiMail, FiPhone, FiMapPin } from 'react-icons/fi';
import flush from 'just-flush';
import { twMerge } from '@stereoagency/tailwind-config';
import Link from 'next/link';
import {
  createDocumentURL,
  findInKeyArray,
  formatPhoneNumberLink,
  formatProfileName,
  sanityImageAsProps
} from '@/utils';
import { SimpleBlockContent } from '@/components/sanity/client';
import { profileModalQueryBuilder } from './profileModalQueryBuilder';

const [, profileType] = profileModalQueryBuilder.first().use();

type Props = {
  profile: typeof profileType | null;
  onClose: () => void;
};

export default function ProfileModal({ profile, onClose }: Props) {
  const { t, lang } = useTranslation();
  const [internalValue, setInternalValue] = useState(profile);
  const groupsAndPlatforms = [
    ...(internalValue?.researchGroups ?? []),
    ...(internalValue?.researchPlatforms ?? [])
  ];

  const subtitle = internalValue?.subtitle
    ? findInKeyArray(internalValue.subtitle, [lang, defaultLanguage.id])
    : null;

  const introduction = internalValue?.introduction
    ? findInKeyArray(internalValue.introduction, [lang, defaultLanguage.id])
    : null;

  const contactInformation = flush([
    internalValue?.email
      ? ([
          FiMail,
          internalValue.email,
          `mailto:${internalValue.email}`
        ] as const)
      : null,
    internalValue?.phone
      ? ([
          FiPhone,
          internalValue.phone,
          formatPhoneNumberLink(internalValue.phone)
        ] as const)
      : null,
    internalValue?.address ? ([FiMapPin, internalValue.address] as const) : null
  ]);

  useEffect(() => {
    if (profile) setInternalValue(profile);
  }, [profile]);

  return (
    <Modal open={!!profile} onClose={onClose}>
      <div
        className={twMerge(
          'grid grid-flow-row auto-rows-max grid-cols-1 gap-6',
          profile?.image?.asset._ref && 'md:grid-cols-[2fr_1fr]'
        )}
      >
        <div className="grid grid-cols-1 grid-flow-row auto-rows-max gap-6 md:gap-9">
          <div>
            <Heading as="h2" variant="h2">
              {internalValue ? formatProfileName(internalValue) : null}
            </Heading>
            {!!subtitle?.value && (
              <Heading as="h3" variant="h3">
                {subtitle?.value}
              </Heading>
            )}
            {!!(introduction?.value || profile?.pubmedQuery) && (
              <Body className="mt-4 prose prose-neutral">
                <SimpleBlockContent value={introduction?.value ?? []} />
                {!!profile?.pubmedQuery && (
                  <a
                    href={`https://pubmed.ncbi.nlm.nih.gov/?term=${encodeURIComponent(
                      profile?.pubmedQuery ?? ''
                    )}`}
                    className="font-semibold"
                    rel="noreferrer"
                    target="_blank"
                  >
                    {t('global:read-more-on-pubmed')}
                  </a>
                )}
              </Body>
            )}
          </div>
          {!!contactInformation.length && (
            <div>
              <Heading as="h3" variant="h3" className="mb-4">
                {t('global:contact')}
              </Heading>
              <div className="grid gap-3 grid-cols-[auto_1fr]">
                {contactInformation.map(([Icon, content, url]) => (
                  <React.Fragment key={content}>
                    <Icon className="w-[1.375rem] h-auto block" />
                    {url ? (
                      <Body className="prose whitespace-pre-line">
                        <Link href={url}>{content}</Link>
                      </Body>
                    ) : (
                      <Body className="prose whitespace-pre-line">
                        {content}
                      </Body>
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>
          )}
          {!!groupsAndPlatforms.length && (
            <div>
              <Heading as="h3" variant="h3" className="mb-4">
                {t('global:research-groups-and-platforms')}
              </Heading>
              <div className="flex flex-wrap items-center justify-start -m-3">
                {groupsAndPlatforms.map((groupOrPlatform) => {
                  const title = groupOrPlatform.title
                    ? findInKeyArray(groupOrPlatform.title, [
                        lang,
                        defaultLanguage.id
                      ])
                    : null;
                  if (!title?.value || !groupOrPlatform.slug?.current)
                    return null;

                  const url = createDocumentURL(
                    lang,
                    groupOrPlatform._type,
                    groupOrPlatform.slug.current
                  );

                  return (
                    <Tag
                      key={url}
                      variant="ghost"
                      href={url}
                      size="md"
                      className="m-3"
                    >
                      {title.value}
                    </Tag>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {!!profile?.image?.asset._ref && (
          <div className="order-first mx-auto w-full max-w-[17rem] md:mr-0 md:mt-auto md:ml-auto md:max-w-none md:order-last">
            <ProfileModalImage
              image={sanityImageAsProps(profile.image)}
              className="md:-mb-11 md:-mr-11"
            />
          </div>
        )}
      </div>
    </Modal>
  );
}
